export type Vec3 = { x: number; y: number; z: number };

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
export const v3add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const v3sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const v3scale = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
export const v3len = (a: Vec3): number => Math.hypot(a.x, a.y, a.z);
export const v3len2 = (a: Vec3): number => a.x * a.x + a.y * a.y + a.z * a.z;
export const v3distSq = (a: Vec3, b: Vec3): number => {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
};

export interface AABB {
  min: Vec3;
  max: Vec3;
}

export const aabbContainsPoint = (box: AABB, p: Vec3): boolean =>
  p.x >= box.min.x && p.x <= box.max.x &&
  p.y >= box.min.y && p.y <= box.max.y &&
  p.z >= box.min.z && p.z <= box.max.z;

export const aabbIntersectsSphere = (box: AABB, center: Vec3, radius: number): boolean => {
  const cx = clamp(center.x, box.min.x, box.max.x);
  const cy = clamp(center.y, box.min.y, box.max.y);
  const cz = clamp(center.z, box.min.z, box.max.z);
  const dx = center.x - cx, dy = center.y - cy, dz = center.z - cz;
  return dx * dx + dy * dy + dz * dz <= radius * radius;
};

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** A tiny mulberry32 PRNG — deterministic, 32-bit, no dependencies. */
export function seededRng(seed: string): () => number {
  let h = 0x9e3779b9 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 0x85ebca6b);
    h ^= h >>> 13;
  }
  let state = h >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function makeSeed(rng: () => number = Math.random): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let s = "";
  for (let i = 0; i < 16; i++) s += chars[Math.floor(rng() * chars.length)];
  return s;
}
